import { useTranslation } from 'react-i18next';
import {
  DeviceJourney,
  LiveNotificationResponse,
  NotificationCounter,
  SimMismatch,
  NotificationType,
  ScheduleJourney,
} from '../../models/NotificationsModels';

function useLiveNotificationsUtils() {
  const [t] = useTranslation();

  const helpUrls = {
    [DeviceJourney.NEW_CONNECTION_PULL]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_new_connection_request.htm',
    [DeviceJourney.NEW_CONNECTION_PUSH]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_new_connection_request.htm',
    [DeviceJourney.UPGRADE_PULL]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_SIM_swap.htm',
    [DeviceJourney.UPGRADE_PUSH]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_SIM_swap.htm',
    [DeviceJourney.LOST_STOLEN_RESTORE]: '/help/Default_CSH.htm',
    [DeviceJourney.LOST_STOLEN_SUSPEND]: '/help/Default_CSH.htm',
    [DeviceJourney.TERMINATION_TERMINATION]: '/help/Default_CSH.htm',
    [DeviceJourney.TERMINATION_SUSPEND]: '/help/Default_CSH.htm',
    [DeviceJourney.TRANSFER_PULL]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_SIM_swap.htm',
    [DeviceJourney.TRANSFER_PUSH]: '/help/Content/Topics/eSIM/eSIM%20Manager/esim_SIM_swap.htm',
  };

  /**
   * Returns the title for the side toast liveNotification
   * @param liveNotification
   */
  const liveNotificationTitleAdapter = (liveNotification: LiveNotificationResponse) =>
    t(`common.liveNotifications.titles.${liveNotification.status}_${liveNotification.type}`);

  /**
   * Checks if it's a device journey notification
   * @param notificationType
   */
  const isDeviceJourney = (notificationType: NotificationType): notificationType is DeviceJourney =>
    Object.values(DeviceJourney).includes(notificationType as DeviceJourney);

  /**
   * Checks if it's a notification counter notification
   * @param notificationType
   */
  const isNotificationCounter = (notificationType: NotificationType): notificationType is NotificationCounter =>
    Object.values(NotificationCounter).includes(notificationType as NotificationCounter);

  /**
   * Checks if it's a sim mismatch notification
   * @param notificationType
   */
  const isSimMismatch = (notificationType: NotificationType): notificationType is SimMismatch =>
    Object.values(SimMismatch).includes(notificationType as SimMismatch);

  /**
   * Checks if it's a schedule journey notification
   * @param notificationType
   */
  const isScheduleJourney = (notificationType: NotificationType): notificationType is ScheduleJourney =>
    Object.values(ScheduleJourney).includes(notificationType as ScheduleJourney);

  /**
   * Checks if it's a schedule new connection notification
   * @param notificationType
   */
  const isScheduleNewConnectionJourney = (notificationType: NotificationType): notificationType is ScheduleJourney =>
    [ScheduleJourney.NEW_CONNECTION_SCHEDULE_1, ScheduleJourney.NEW_CONNECTION_SCHEDULE_2].includes(
      notificationType as ScheduleJourney,
    );

  return {
    isNotificationCounter,
    isDeviceJourney,
    isSimMismatch,
    isScheduleJourney,
    liveNotificationTitleAdapter,
    helpUrls,
    isScheduleNewConnectionJourney,
  };
}

export default useLiveNotificationsUtils;
